import React from "react";
import { formatNumber, formatDate } from "@/lib/utils";
import { getCompanyConfig } from "@/config/company";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

interface StocktakeBalancePrintTemplateProps {
  data?: any;
  config?: any;
}

export default function StocktakeBalancePrintTemplate({
  data = {},
  config,
}: StocktakeBalancePrintTemplateProps) {
  const c = getCompanyConfig(config);
  const docId = data?.code || data?.id || "";
  const lines = data?.lines || data?.items || [];
  const lineQty = (line: any) => {
    const system = Number(line.systemQty ?? line.theoreticalQty ?? 0);
    const actual = Number(line.actualQty ?? line.countedQty ?? 0);
    return { system, actual, diff: actual - system };
  };
  const lineDiffValue = (line: any) =>
    line.diffValue != null ? Number(line.diffValue) : lineQty(line).diff * Number(line.unitPrice || 0);
  const totalDiffValue = lines.reduce((sum: number, line: any) => sum + lineDiffValue(line), 0);

  return (
    <div
      className="p-[10mm] text-black bg-white w-full h-full mx-auto"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="print-area"
    >
      <style>{`
        .iso-table td, .iso-table th { border: 1px solid #000; padding: 6px; }
        .stocktake-lines { table-layout: fixed; }
        .stocktake-lines td, .stocktake-lines th { padding: 3px 4px; }
        .bg-gray-header { background-color: #f1f5f9; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      `}</style>

      {/* 1. HEADER CÔNG TY */}
      <ISOPrintHeader 
        config={config} 
      />

      {/* 2. TIÊU ĐỀ BIỂU MẪU (CENTERED, NO QR) */}
      <PrintDocumentTitle
        title="BIÊN BẢN KIỂM KÊ KHO"
        subtitle="STOCKTAKE AUDIT REPORT"
        docNo={docId}
        date={data.checkDate || data.createdAt || new Date()}
        dateLabel="Thời điểm kiểm kê"
      />

      {/* 3. THÔNG TIN HỘI ĐỒNG KIỂM KÊ & KHO (BORDERLESS 2-COL TABS) */}
      <div className="mb-4">
        <PrintInfoGrid
          labelWidth="w-[125px]"
          leftColumn={[
            {
              label: "Kho kiểm kê",
              value:
                data.warehouse?.name ||
                data.warehouseName ||
                (data.type === "FG"
                  ? "Kho Thành Phẩm Chính"
                  : data.type === "TECH" || data.type === "TOOLING"
                    ? "Kho Kỹ Thuật (Khuôn & Bản Kẽm)"
                    : "Kho Vật Tư Chính"),
              isBold: true,
            },
            {
              label: "Người lập phiếu",
              value: data.creator?.fullName || data.createdByName || "—",
            },
            {
              label: "Trạng thái phiếu",
              value:
                data.status === "COMPLETED"
                  ? "Đã chốt & cân đối tồn kho"
                  : data.status === "CANCELLED"
                    ? "Đã hủy (Mở khóa kho)"
                    : "Đang kiểm đếm (Đang khóa kho)",
            },
          ]}
          rightColumn={[
            {
              label: "Chứng từ cân đối",
              value:
                data.receiptDocNo || data.issueDocNo
                  ? [
                      data.receiptDocNo ? `Phiếu Nhập: ${data.receiptDocNo}` : null,
                      data.issueDocNo ? `Phiếu Xuất: ${data.issueDocNo}` : null,
                    ]
                      .filter(Boolean)
                      .join(" • ")
                  : data.status === "COMPLETED"
                    ? "Khớp sổ 100% (Không phát sinh lệch)"
                    : "Chưa chốt kiểm kê",
              isBold: Boolean(data.receiptDocNo || data.issueDocNo),
            },
            {
              label: "Mục đích",
              value: "Kiểm kê định kỳ & đối soát số dư thực tế",
            },
            {
              label: "Ghi chú đợt kiểm",
              value: data.notes || "Kiểm đếm 100% các vị trí kệ và kiện pallet",
            },
          ]}
        />
      </div>

      {/* 4. BẢNG CHI TIẾT ĐỐI SOÁT TỒN KHO */}
      <div className="mb-4">
        <div className="bg-slate-100 font-bold text-[12px] px-2 py-1 border border-black uppercase">
          2. KẾT QUẢ ĐỐI SOÁT SỔ SÁCH ERP VÀ THỰC ĐẾM
        </div>
        <table className="w-full iso-table stocktake-lines text-left border-collapse border border-black border-t-0 text-[10px] font-sans">
          <colgroup>
            {[5, 13, 22, 5, 8, 9, 7, 7, 7, 8, 9].map((w, i) => (
              <col key={i} style={{ width: `${w}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-slate-50 font-bold text-center border-b border-black">
              <th>STT</th>
              <th className="text-left">Mã hàng</th>
              <th className="text-left">Tên hàng &amp; Quy cách</th>
              <th>ĐVT</th>
              <th className="text-left">Vị trí</th>
              <th className="text-right">Tồn sổ sách</th>
              <th className="text-right">Thực đếm</th>
              <th className="text-right">Chênh lệch</th>
              <th className="text-right">Giá trị lệch</th>
              <th className="text-left">Ghi chú</th>
            </tr>
          </thead>
          <tbody>
            {lines.length > 0 ? (
              lines.map((line: any, idx: number) => {
                const { system, actual, diff } = lineQty(line);
                const diffValue = lineDiffValue(line);
                return (
                  <tr key={idx} className="border-b border-black last:border-b-0">
                    <td className="text-center font-mono whitespace-nowrap">{idx + 1}</td>
                    <td className="font-mono font-bold text-indigo-900 break-all">
                      {line.itemCode || line.material?.materialId || line.materialId || "—"}
                    </td>
                    <td>
                      <div className="font-bold">{line.itemName || line.material?.name || line.name || "Vật tư"}</div>
                      {line.specs && <div className="text-[10px] text-slate-500 font-mono">{line.specs}</div>}
                    </td>
                    <td className="text-center whitespace-nowrap">{line.unit || line.material?.unit || "Tờ"}</td>
                    <td className="break-words">{line.locationName || "—"}</td>
                    <td className="text-right font-mono whitespace-nowrap">{formatNumber(system)}</td>
                    <td className="text-right font-mono font-bold text-blue-700 whitespace-nowrap">
                      {formatNumber(actual)}
                    </td>
                    <td
                      className={`text-right font-mono font-bold whitespace-nowrap ${diff < 0 ? "text-red-600" : diff > 0 ? "text-emerald-700" : "text-slate-800"}`}
                    >
                      {diff > 0 ? `+${formatNumber(diff)}` : formatNumber(diff)}
                    </td>
                    <td className="text-right font-mono whitespace-nowrap">{formatNumber(Math.round(diffValue))}</td>
                    <td className="text-slate-700">
                      {line.reason || line.notes || (diff === 0 ? "Khớp sổ" : diff > 0 ? "Thừa thực tế (Nhập cân đối)" : "Thiếu hụt (Xuất cân đối)")}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={10} className="text-center italic py-4 text-slate-500">
                  Không có dữ liệu mặt hàng trong đợt kiểm kê này.
                </td>
              </tr>
            )}
          </tbody>
          {lines.length > 0 && (
            <tfoot>
              <tr className="font-bold bg-slate-50">
                <td colSpan={8} className="text-right">Tổng giá trị chênh lệch ({lines.length} dòng)</td>
                <td className="text-right font-mono whitespace-nowrap">{formatNumber(Math.round(totalDiffValue))}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* 5. CHỮ KÝ 4 BÊN */}
      <div className="grid grid-cols-4 gap-2 mt-8 text-[12px] text-center break-inside-avoid font-sans">
        <div>
          <div className="font-bold uppercase">THỦ KHO</div>
          <div className="text-slate-500 italic text-[11px] mb-14">(Ký &amp; ghi rõ họ tên)</div>
          <div className="font-semibold">&nbsp;</div>
        </div>
        <div>
          <div className="font-bold uppercase">KẾ TOÁN KHO / ĐỐI SOÁT</div>
          <div className="text-slate-500 italic text-[11px] mb-14">(Ký &amp; ghi rõ họ tên)</div>
          <div className="font-semibold">{data.creator?.fullName || data.createdByName || "—"}</div>
        </div>
        <div>
          <div className="font-bold uppercase">TRƯỞNG BAN KIỂM KÊ</div>
          <div className="text-slate-500 italic text-[11px] mb-14">(Ký &amp; ghi rõ họ tên)</div>
          <div className="font-semibold">&nbsp;</div>
        </div>
        <div>
          <div className="font-bold uppercase">GIÁM ĐỐC NHÀ MÁY</div>
          <div className="text-slate-500 italic text-[11px] mb-14">(Phê duyệt điều chỉnh)</div>
          <div className="font-semibold">&nbsp;</div>
        </div>
      </div>
    </div>
  );
}
